// src/components/demo/DemoRoleSwitcher.js

import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useDemo } from "../../hooks/useDemo";
import { useAuth } from "../../hooks/useAuth";
import { usePeptideCart } from "../../hooks/usePeptideCart";
import { filterDemoRoleGroups } from "../../utils/productLineUtils";
import "../../scss/demo.scss";

function DemoRoleSwitcher() {
  const { t } = useTranslation();
  const { isDemoMode, demoRole, switchRole, realProductLines } = useDemo();
  const { auth } = useAuth();
  const location = useLocation();
  const { itemCount } = usePeptideCart();
  // Lift the pill only when the StickyCartBar is actually on screen — that
  // bar mounts inside PeptideCatalog and renders only when the cart has
  // items (StickyCartBar.js bails on itemCount === 0). Anywhere else the
  // pill stays at its default bottom-right slot, so empty-cart browsing,
  // checkout, and non-peptide pages aren't pushed around.
  const queryView = new URLSearchParams(location.search).get("view");
  const stickyCartBarVisible =
    location.pathname.startsWith("/peptides") && (queryView === "catalog" || queryView == null) && itemCount > 0;
  // Filter by the user's REAL effective product lines (captured at enterDemo),
  // not the current fake demo auth. Two reasons:
  //   1) Switching into a DME-only persona swaps in a fake auth whose orgInfo
  //      has no productLines field, which would legacy-fallback to DME-only
  //      and hide the Peptides bucket for a dual-line user.
  //   2) The captured lens is the user's EFFECTIVE lines (org ∩ user), so a
  //      peptides-only sales rep on a dual-line org gets the Peptides bucket
  //      only — not both. See `getDemoLensProductLines` in productLineUtils.
  const filterProductLines = realProductLines || auth?.orgInfo?.productLines;

  const ROLE_GROUPS_ALL = [
    {
      key: "dme",
      label: t("demo.roleGroups.dme", "DME"),
      roles: [
        { key: "provider", icon: "local_hospital", name: t("demo.roles.provider"), desc: t("demo.roles.providerDesc") },
        {
          key: "salesDashboard",
          icon: "trending_up",
          name: t("demo.roles.salesDashboard"),
          desc: t("demo.roles.salesDashboardDesc"),
        },
        {
          key: "manufacturer",
          icon: "precision_manufacturing",
          name: t("demo.roles.manufacturer"),
          desc: t("demo.roles.manufacturerDesc"),
        },
      ],
    },
    {
      key: "peptides",
      label: t("demo.roleGroups.peptides", "Peptides"),
      roles: [
        {
          key: "peptidesProvider",
          icon: "local_hospital",
          name: t("demo.roles.peptidesProvider", "Peptide Provider"),
          desc: t("demo.roles.peptidesProviderDesc", "Clinic ordering peptides for patients"),
        },
        {
          key: "peptidesManufacturer",
          icon: "precision_manufacturing",
          name: t("demo.roles.peptidesManufacturer", "Peptide Manufacturer"),
          desc: t("demo.roles.peptidesManufacturerDesc", "Manage catalog, pricing, fulfillment, analytics"),
        },
        {
          key: "peptidesPatient",
          icon: "person",
          name: t("demo.roles.peptidesPatient", "Peptide Patient"),
          desc: t("demo.roles.peptidesPatientDesc", "Patient self-serve checkout flow"),
        },
        {
          key: "peptidesSalesDashboard",
          icon: "trending_up",
          name: t("demo.roles.peptidesSalesDashboard", "Peptide Sales Rep"),
          desc: t("demo.roles.peptidesSalesDashboardDesc", "Sales dashboard + personal peptide ordering"),
        },
      ],
    },
  ];

  const ROLE_GROUPS = filterDemoRoleGroups(ROLE_GROUPS_ALL, filterProductLines);

  const ALL_ROLES = ROLE_GROUPS.flatMap((g) => g.roles);

  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setExpanded(false);
      }
    }
    if (expanded) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [expanded]);

  if (!isDemoMode) return null;

  const currentRole = ALL_ROLES.find((r) => r.key === demoRole) || ALL_ROLES[0];
  // Orphan rep edge: user with both product lines revoked
  // (`custom.productLines: {dme:false, peptides:false}`) yields an empty
  // ROLE_GROUPS after filtering. Render nothing rather than crash on
  // `currentRole.icon` below. Exit-demo still works via DemoBanner.
  if (!currentRole) return null;

  return (
    <div className={`demo-role-switcher${stickyCartBarVisible ? " demo-role-switcher--lifted" : ""}`} ref={ref}>
      {expanded && (
        <div className="demo-role-dropdown">
          <div className="demo-role-dropdown-header">{t("demo.switchRole")}</div>
          {ROLE_GROUPS.map((group) => (
            <div key={group.key} className="demo-role-group">
              <div className="demo-role-group-label">{group.label}</div>
              {group.roles.map((role) => (
                <div
                  key={role.key}
                  className={"demo-role-option" + (role.key === demoRole ? " active" : "")}
                  onClick={() => {
                    switchRole(role.key);
                    setExpanded(false);
                  }}
                >
                  <span className="demo-role-option-icon">
                    <i className="notranslate material-icons-outlined">{role.icon}</i>
                  </span>
                  <div>
                    <div className="demo-role-option-name">{role.name}</div>
                    <div className="demo-role-option-desc">{role.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <div role="button" className={"demo-role-pill" + (expanded ? " expanded" : "")} onClick={() => setExpanded(!expanded)}>
        <i className="notranslate material-icons-outlined demo-role-pill-icon">{currentRole.icon}</i>
        <span>{currentRole.name}</span>
        <i className="notranslate material-icons-outlined demo-role-pill-chevron">expand_less</i>
      </div>
    </div>
  );
}

export default DemoRoleSwitcher;
